import { AxiosRequestConfig } from 'axios';
import { axiosClient } from '@as-react/common-utils';
import { AddressFieldsType, GeoLocation, UKAddress, USState } from '@as-react/common-types';
import { capitalize } from '@as-react/common-utils';
import { createUrl } from '../../utils/createUrl';
import { QueryApiOptions, UrlMapping } from '../../types/types';
import { generalQueryKeys } from '../..';
import { QueryFunctionContext } from '@tanstack/react-query';

export type GeneralUSStatesParams = {
  countryId?: number;
};

export type GeneralAddressesParams = {
  countryId: number;
  postalCode: string;
  houseNumber?: string;
};

export type GeneralCompanyDetailsByVATParams = {
  vatNumber: string;
  country: string;
};

export type GeneralUKAddressListParams = {
  term: string;
  size?: number;
};

export type GeneralUKAddressDetailsParams = {
  moniker: string;
};

export type GetGeoLocationParams = {
  countryCode: string;
  location: string;
};

const generalEndpoints: UrlMapping<typeof generalQueryKeys> = {
  USStates: {
    client: 'api/general/county/country',
  },
  addresses: {
    client: 'api/aem/general/postal/{postalCode}/{houseNumber}',
  },
  getGeoLocation: {
    client: 'api/aem/general/geocode/components',
  },
  companyDetailsByVAT: {
    client: 'api/general/vat/validate',
  },
  UKAddressList: {
    client: 'api/aem/general/postal/v2/uk/search',
  },
  UKAddressDetails: {
    client: 'api/aem/general/postal/v2/uk/format',
  },
};

export const getUsStates = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { countryId }: GeneralUSStatesParams
): Promise<Array<USState>> => {
  if (!countryId) {
    return [];
  }

  const url = createUrl(servicesEndpoint, generalEndpoints.USStates, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: { ...defaultRequestParams, countryId },
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const getAddresses = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { countryId, postalCode, houseNumber }: GeneralAddressesParams
): Promise<Array<AddressFieldsType>> => {
  const url = createUrl(servicesEndpoint, generalEndpoints.addresses, isServer, {
    postalCode,
    houseNumber,
  });
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: { ...defaultRequestParams, countryId, language: undefined },
  };

  const result = await axiosClient.request<{ addresses: Array<AddressFieldsType> } | Array<AddressFieldsType>>(
    axiosConfig
  );

  if ('addresses' in result.data) {
    return result.data.addresses.map(address => {
      if (address.city) {
        address.city = capitalize(address.city);
      }
      return address;
    });
  }

  return result.data;
};

export const getGeoLocation = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { countryCode, location }: GetGeoLocationParams
): Promise<GeoLocation> => {
  const url = createUrl(servicesEndpoint, generalEndpoints.getGeoLocation, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: {
      countryCode,
      languageCode: defaultRequestParams.language,
      addressQuery: location,
    },
    withCredentials: true,
  };

  const answer = await axiosClient.request(axiosConfig);
  return answer.data;
};

export const getCompanyDetailsByVAT = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { vatNumber, country }: GeneralCompanyDetailsByVATParams
) => {
  const url = createUrl(servicesEndpoint, generalEndpoints.companyDetailsByVAT, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: { ...defaultRequestParams, vatnumber: vatNumber, country },
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const getUKAddressList = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { term, size }: GeneralUKAddressListParams,
  { signal }: QueryFunctionContext
): Promise<Array<UKAddress>> => {
  const url = createUrl(servicesEndpoint, generalEndpoints.UKAddressList, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    signal,
    params: { ...defaultRequestParams, term, size },
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data.pickList;
};

export const getUKAddressDetails = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { moniker }: GeneralUKAddressDetailsParams
): Promise<AddressFieldsType> => {
  const url = createUrl(servicesEndpoint, generalEndpoints.UKAddressDetails, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: { ...defaultRequestParams, moniker },
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};
